import { Eye, EyeOff, Loader, Lock, Mail, Globe } from "lucide-react";
import { useState, useEffect } from "react";

import translations from "./Constant/Login.translations";
import getResponsiveStyles from "./Style/Login.styles";
import useLoginForm from "./hooks/useLoginForm";
import SplashOverlay from "./components/SplashOverlay";
import ResetPasswordModal from "./components/ResetPasswordModal";
import ForceChangePasswordModal from "./components/ForceChangePasswordModal";
import Iconee from "../../Images/photos/icon.png";

const LoginPage = () => {
  // ─── Language ──────────────────────────────────────────────────────────────
  const [language, setLanguage] = useState("en");
  const t = translations[language];
  const toggleLanguage = () =>
    setLanguage((prev) => (prev === "en" ? "ar" : "en"));

  // ─── Focus state ──────────────────────────────────────────────────────────
  const [isEmailFocused, setIsEmailFocused] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // ─── Screen Size ──────────────────────────────────────────────────────────
  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
    isMobile: window.innerWidth <= 768,
    isTablet: window.innerWidth > 768 && window.innerWidth <= 1024,
    isDesktop: window.innerWidth > 1024,
    isSmallScreen: window.innerHeight <= 600,
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      setScreenSize({
        width,
        height,
        isMobile: width <= 768,
        isTablet: width > 768 && width <= 1024,
        isDesktop: width > 1024,
        isSmallScreen: height <= 600,
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ─── Form Logic ────────────────────────────────────────────────────────────
  const {
    logoutNotice,
    email, setEmail,
    password, setPassword,
    rememberMe, setRememberMe,
    error,
    success,
    isLoading,
    splashOverlay,
    lockoutSecondsLeft,
    isLongLockout,
    isAccountLocked,
    lockoutBlocksCurrent,
    formatCountdown,
    handleSubmit,
    resendCooldownSeconds,
    isResetModalOpen,
    resetEmail, setResetEmail,
    resetStatus,
    resetMessage,
    isResetLoading,
    handleOpenResetModal,
    handleCloseResetModal,
    handleResetPasswordSubmit,
    handleResetKeyPress,
    isForcePasswordChangeOpen,
    forcePasswordChangeError,
    isForcePasswordChangeLoading,
    handleForcePasswordChangeSubmit,
    handleForcePasswordChangeLogoutInstead,
  } = useLoginForm(t);

  // ─── Styles ────────────────────────────────────────────────────────────────
  const styles = getResponsiveStyles(screenSize, language);

  return (
    <>
      {/* Splash Overlay */}
      <SplashOverlay
        splashOverlay={splashOverlay}
        screenSize={screenSize}
        t={t}
        styles={styles}
      />

      <div style={styles.container}>
        {/* Language Toggle */}
        <button
          onClick={toggleLanguage}
          style={styles.languageToggle}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#084d98";
            e.currentTarget.style.color = "white";
            e.currentTarget.style.transform = "translateY(-2px)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor =
              "rgba(255, 255, 255, 0.95)";
            e.currentTarget.style.color = "#084d98";
            e.currentTarget.style.transform = "translateY(0)";
          }}
        >
          <Globe size={18} />
          {language === "en" ? "العربية" : "English"}
        </button>

        {/* Background */}
        <div style={styles.backgroundImage} />
        <div style={styles.backgroundDecor}>
          <div style={styles.circle1} />
          <div style={styles.circle2} />
        </div>

        {/* Login Card */}
        <div style={styles.loginCard}>
          {/* Header */}
          <header style={styles.header}>
            <div style={styles.logo}>
              <img src={Iconee} alt="Infinity Logo" style={styles.logoImg} />
            </div>
            <h2 style={styles.title}>{t.welcome}</h2>
            <p style={styles.subtitle}>{t.signInAccount}</p>
          </header>

          {/* Form */}
          <form onSubmit={handleSubmit} style={styles.form}>
            {/* Email */}
            <div style={styles.fieldGroup}>
              <label style={styles.label}>{t.emailAddress}</label>
              <div style={styles.inputWrapper}>
                <Mail style={styles.inputIcon} />
                <input
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoCapitalize="none"
                  onFocus={() => setIsEmailFocused(true)}
                  onBlur={() => setIsEmailFocused(false)}
                  style={{
                    ...styles.input,
                    boxShadow: isEmailFocused
                      ? "0 0 12px rgba(59, 130, 246, 0.8)"
                      : "none",
                    border: isEmailFocused
                      ? "1px solid rgba(59, 130, 246, 0.6)"
                      : styles.input.border,
                  }}
                  placeholder={t.enterEmail}
                />
              </div>
            </div>

            {/* Password */}
            <div style={styles.fieldGroup}>
              <label style={styles.label}>{t.password}</label>
              <div style={styles.inputWrapper}>
                <Lock style={styles.inputIcon} />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  onFocus={() => setIsPasswordFocused(true)}
                  onBlur={() => setIsPasswordFocused(false)}
                  style={{
                    ...styles.input,
                    ...styles.inputPassword,
                    boxShadow: isPasswordFocused
                      ? "0 0 12px rgba(59, 130, 246, 0.8)"
                      : "none",
                    border: isPasswordFocused
                      ? "1px solid rgba(59, 130, 246, 0.6)"
                      : styles.input.border,
                  }}
                  placeholder={t.enterPassword}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  style={styles.showPasswordBtn}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeOff size={screenSize.isMobile ? 18 : 20} />
                  ) : (
                    <Eye size={screenSize.isMobile ? 18 : 20} />
                  )}
                </button>
              </div>
            </div>

            {/* Remember Me + Forgot Password */}
            <div style={styles.rememberSection}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  style={styles.checkbox}
                />
                {t.rememberMe}
              </label>
              <a
                href="#"
                style={styles.forgotLink}
                onClick={handleOpenResetModal}
              >
                {t.forgotPassword}
              </a>
            </div>

            {/* Why the last session ended (server-ended sessions only) */}
            {logoutNotice && !error && (
              <div
                role="status"
                style={
                  logoutNotice === "account_inactive"
                    ? { ...styles.notice, ...styles.noticeWarning }
                    : styles.notice
                }
              >
                <div style={{ fontWeight: 700 }}>
                  {logoutNotice === "account_inactive" ? t.accountInactiveTitle : t.sessionEndedTitle}
                </div>
                <div style={{ marginTop: 2 }}>
                  {logoutNotice === "account_inactive" ? t.accountInactiveBody : t.sessionEndedBody}
                </div>
              </div>
            )}

            {/* Error / Success */}
            {error && (
              <div style={styles.error}>
                {error}
                {lockoutSecondsLeft > 0 && (
                  <div style={{ marginTop: 6, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                    {(isAccountLocked ? t.lockedTryAgainIn : t.tryAgainIn) || "Try again in"}{" "}
                    {formatCountdown(lockoutSecondsLeft)}
                  </div>
                )}
                {isLongLockout && (
                  <div style={{ marginTop: 6 }}>
                    {t.longLockoutHint ||
                      "This wait is long because of repeated failed attempts. Use Forgot Password to regain access immediately."}{" "}
                    <a href="#" style={styles.forgotLink} onClick={handleOpenResetModal}>
                      {t.forgotPassword}
                    </a>
                  </div>
                )}
              </div>
            )}
            {success && <div style={styles.success}>{success}</div>}

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading || lockoutBlocksCurrent}
              style={{
                ...styles.submitBtn,
                opacity: isLoading || lockoutBlocksCurrent ? 0.6 : 1,
                cursor: isLoading || lockoutBlocksCurrent ? "not-allowed" : "pointer",
              }}
              onMouseEnter={(e) => {
                if (!isLoading && !lockoutBlocksCurrent) {
                  e.currentTarget.style.background =
                    "linear-gradient(135deg, #2e7cba, #2e7cba)";
                  e.currentTarget.style.transform = "scale(1.02)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.background =
                    "linear-gradient(135deg, #2e7cba, #084d98ff)";
                  e.currentTarget.style.transform = "scale(1)";
                }
              }}
            >
              {isLoading ? (
                <>
                  <Loader
                    size={screenSize.isMobile ? 18 : 20}
                    className="animate-spin"
                  />
                  {t.processing}
                </>
              ) : lockoutBlocksCurrent ? (
                `${t.lockedButton || "Locked"} · ${formatCountdown(lockoutSecondsLeft)}`
              ) : (
                t.signIn
              )}
            </button>
          </form>
        </div>

        {/* Global Styles */}
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          .animate-spin {
            animation: spin 1s linear infinite;
          }
          input::placeholder {
            color: #084d98ff;
            opacity: 0.6;
          }
          @media (max-width: 768px) {
            ::-webkit-scrollbar { width: 4px; }
            ::-webkit-scrollbar-track {
              background: rgba(255, 255, 255, 0.1);
              border-radius: 2px;
            }
            ::-webkit-scrollbar-thumb {
              background: rgba(255, 255, 255, 0.3);
              border-radius: 2px;
            }
            ::-webkit-scrollbar-thumb:hover {
              background: rgba(255, 255, 255, 0.5);
            }
          }
          button:focus-visible, input:focus-visible {
            outline: 2px solid rgba(59, 130, 246, 0.8);
            outline-offset: 2px;
          }
          * { -webkit-tap-highlight-color: transparent; }
          @media screen and (-webkit-min-device-pixel-ratio: 0) {
            select, textarea, input[type="text"], input[type="password"],
            input[type="datetime"], input[type="datetime-local"],
            input[type="date"], input[type="month"], input[type="time"],
            input[type="week"], input[type="number"], input[type="email"],
            input[type="url"], input[type="search"], input[type="tel"] {
              font-size: 16px !important;
            }
          }
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>

        {/* Reset Password Modal */}
        <ResetPasswordModal
          isOpen={isResetModalOpen}
          onClose={handleCloseResetModal}
          t={t}
          styles={styles}
          screenSize={screenSize}
          resetEmail={resetEmail}
          setResetEmail={setResetEmail}
          resetStatus={resetStatus}
          resetMessage={resetMessage}
          isResetLoading={isResetLoading}
          resendCooldownSeconds={resendCooldownSeconds}
          onSubmit={handleResetPasswordSubmit}
          onKeyPress={handleResetKeyPress}
        />

        <ForceChangePasswordModal
          isOpen={isForcePasswordChangeOpen}
          t={t}
          language={language}
          styles={styles}
          isLoading={isForcePasswordChangeLoading}
          error={forcePasswordChangeError}
          onSubmit={handleForcePasswordChangeSubmit}
          onLogoutInstead={handleForcePasswordChangeLogoutInstead}
        />
      </div>
    </>
  );
};

export default LoginPage;